/**
 * Partner Subdomain Utilities
 *
 * Utilities for extracting and managing partner information from the current URL.
 * This enables dynamic partner subdomains (e.g., jean.easyservicebf.com) to be
 * automatically detected and used in API requests.
 *
 * @module utils/partner
 * @author EASYSERVICE
 * @version 1.0.0
 */

// ============================================================================
// TYPES
// ============================================================================

/**
 * Partner information extracted from the current URL
 *
 * @interface PartnerData
 * @property {string | null} partner - The partner subdomain (e.g., "jean", "marie") or null if no subdomain
 * @property {string} url - The full URL of the current page
 * @property {string} host - The hostname including subdomain (e.g., "jean.easyservicebf.com")
 * @property {string} domain - The main domain without subdomain (e.g., "easyservicebf.com")
 * @property {number} timestamp - Unix timestamp when the data was extracted
 * @property {boolean} isPartnerSite - Whether the current site is a partner subdomain
 * @property {boolean} isProduction - Whether the site is in production environment
 */
export interface PartnerData {
  partner: string | null;
  url: string;
  host: string;
  domain: string;
  timestamp: number;
  isPartnerSite: boolean;
  isProduction: boolean;
}

/**
 * Options for partner data extraction
 *
 * @interface PartnerOptions
 * @property {boolean} includeTimestamp - Whether to include timestamp in result (default: true)
 * @property {boolean} ignoreWww - Whether to ignore "www" as a subdomain (default: true)
 * @property {string[]} customDomains - Additional custom domains to recognize (default: [])
 */
export interface PartnerOptions {
  includeTimestamp?: boolean;
  ignoreWww?: boolean;
  customDomains?: string[];
}

// ============================================================================
// CONFIGURATION
// ============================================================================

/**
 * Default configuration for partner extraction
 */
const DEFAULT_CONFIG: Required<PartnerOptions> = {
  includeTimestamp: true,
  ignoreWww: true,
  customDomains: [],
};

/**
 * Known production domains for the application
 */
const PRODUCTION_DOMAINS = [
  'easyservicebf.com',
  'easyservice.web.app',
  'web.app',
];

/**
 * Known development/localhost patterns
 */
const LOCALHOST_PATTERNS = [
  'localhost',
  '127.0.0.1',
  '0.0.0.0',
  '[::1]',
];

// ============================================================================
// MAIN FUNCTIONS
// ============================================================================

/**
 * Extracts the partner subdomain from the current URL
 *
 * This function analyzes the current window location and extracts the partner
 * subdomain if present. It handles edge cases like www, localhost, and the main domain.
 *
 * @param {Window} [windowObject=window] - The window object (for testing)
 * @param {PartnerOptions} [options={}] - Configuration options
 * @returns {string | null} - The partner subdomain or null if not found
 *
 * @example
 * // On jean.easyservicebf.com
 * getPartnerFromUrl(); // Returns: "jean"
 *
 * @example
 * // On www.easyservicebf.com
 * getPartnerFromUrl(); // Returns: null (www is ignored by default)
 *
 * @example
 * // On easyservicebf.com
 * getPartnerFromUrl(); // Returns: null (no subdomain)
 *
 * @example
 * // On localhost:3000
 * getPartnerFromUrl(); // Returns: null (localhost)
 *
 * @example
 * // On easyservicebf.web.app/234567
 * getPartnerFromUrl(); // Returns: null (easyservicebf is the app name, not a partner)
 */
export function getPartnerFromUrl(
  windowObject: Window = window,
  options: PartnerOptions = {}
): string | null {
  const config = { ...DEFAULT_CONFIG, ...options };
  const hostname = windowObject.location.hostname.toLowerCase();

  // Handle localhost scenarios
  if (isLocalhost(hostname)) {
    return null;
  }

  // Extract subdomain from hostname
  const parts = hostname.split('.');

  // Must have at least 3 parts for subdomain.domain.tld
  if (parts.length < 3) {
    return null;
  }

  // Get the potential subdomain (first part)
  const subdomain = parts[0];

  // Ignore www if configured
  if (config.ignoreWww && subdomain === 'www') {
    return null;
  }

  // Ignore "easyservicebf" as it's the app name, not a partner subdomain
  if (subdomain === 'easyservicebf' || subdomain === 'easyservice') {
    return null;
  }

  // Check if this is a known production domain
  const mainDomain = parts.slice(-2).join('.');
  const allDomains = [...PRODUCTION_DOMAINS, ...config.customDomains];

  const isKnownDomain = allDomains.some(domain =>
    hostname.endsWith(`.${domain}`) || hostname === domain
  );

  // Only return subdomain if it's on a known domain and subdomain exists
  if (isKnownDomain && subdomain) {
    return subdomain;
  }

  return null;
}

/**
 * Extracts the commercial ID from the URL path
 *
 * This function extracts the commercial ID (partner code) from the URL path.
 * It handles patterns like /1234567, /share/1234567, /:commercialId, etc.
 *
 * @param {Window} [windowObject=window] - The window object (for testing)
 * @returns {string | null} - The commercial ID or null if not found
 *
 * @example
 * // On https://easyservicebf.web.app/234567
 * getCommercialIdFromPath(); // Returns: "234567"
 *
 * @example
 * // On https://easyservicebf.web.app/share/234567
 * getCommercialIdFromPath(); // Returns: "234567"
 *
 * @example
 * // On https://easyservicebf.com/pubfb/dashboard
 * getCommercialIdFromPath(); // Returns: "pubfb"
 *
 * @example
 * // On https://easyservicebf.com/
 * getCommercialIdFromPath(); // Returns: null
 *
 * @example
 * // On https://easyservicebf.com/payment  (known app route)
 * getCommercialIdFromPath(); // Returns: null (route name is NOT a commercial id)
 */

/**
 * Routes de l'application qui ne doivent JAMAIS être interprétées comme
 * commercial_id. Le path /payment de l'app n'a rien à voir avec un code
 * commercial. Maintenue à la main — à synchroniser avec les <Route> de App.tsx.
 */
const APP_ROUTES = new Set<string>([
  'login',
  'register',
  'jeux',
  'account',
  'transactions',
  'lottery',
  'deposit',
  'payment',
  'payment-confirmation',
  'withdraw',
  'coupons',
  'recharge-coupon',
  'scratch-card',
  'parrainage',
  'affiliate-withdraw',
  'install',
  'dashboard',
  'share',
]);

export function getCommercialIdFromPath(
  windowObject: Window = window
): string | null {
  const pathname = windowObject.location.pathname;

  // Remove leading and trailing slashes
  const cleanPath = pathname.replace(/^\/|\/$/g, '');

  // Split by slash
  const parts = cleanPath.split('/').filter(Boolean);

  if (parts.length === 0) {
    return null;
  }

  const firstPart = parts[0];

  // Reject known app routes outright (handles Bug #1: "/payment" must NOT
  // resolve to commercial_id "payment").
  if (APP_ROUTES.has(firstPart.toLowerCase())) {
    return null;
  }

  // Numeric commercial id (Directus pattern: 7-15 digits)
  if (/^\d{7,15}$/.test(firstPart)) {
    return firstPart;
  }

  // /share/:commercialId
  if (
    firstPart.toLowerCase() === 'share' &&
    parts.length > 1 &&
    /^\d{7,15}$/.test(parts[1])
  ) {
    return parts[1];
  }

  // Alphanumeric partner code on a dedicated partner route (e.g. /pubfb/...).
  // We require 3-20 chars AND that the segment looks like an explicit code:
  // must start with a letter (rules out accidental matches) and contain at
  // least one letter or digit mix that excludes simple lowercase words.
  if (/^[a-z][a-z0-9]{2,19}$/i.test(firstPart)) {
    return firstPart;
  }

  return null;
}

/**
 * Gets the commercial ID from either subdomain or path
 *
 * This is a convenience function that checks both the URL subdomain
 * and the path to find the commercial ID.
 *
 * @param {Window} [windowObject=window] - The window object (for testing)
 * @param {PartnerOptions} [options={}] - Configuration options
 * @returns {string | null} - The commercial ID or null if not found
 *
 * @example
 * // On https://pubfb.easyservicebf.com
 * getCommercialId(); // Returns: "pubfb"
 *
 * @example
 * // On https://easyservicebf.web.app/234567
 * getCommercialId(); // Returns: "234567"
 *
 * @example
 * // On https://easyservicebf.web.app/share/234567
 * getCommercialId(); // Returns: "234567"
 *
 * @example
 * // On https://easyservicebf.com
 * getCommercialId(); // Returns: null
 */
export function getCommercialId(
  windowObject: Window = window,
  options: PartnerOptions = {}
): string | null {
  // First try to get from subdomain
  const fromSubdomain = getPartnerFromUrl(windowObject, options);
  if (fromSubdomain) {
    return fromSubdomain;
  }

  // Then try to get from path
  const fromPath = getCommercialIdFromPath(windowObject);
  if (fromPath) {
    return fromPath;
  }

  return null;
}

/**
 * Lit le commercial_id présent dans l'URL courante : sous-domaine partenaire
 * en priorité, sinon paramètre de requête `commercialId`. Le chemin n'est
 * volontairement pas lu ici (risque de confondre avec une route de l'app).
 */
export function getCommercialIdFromUrl(
  windowObject: Window = window,
  options: PartnerOptions = {}
): string | null {
  const fromSubdomain = getPartnerFromUrl(windowObject, options);
  if (fromSubdomain) return fromSubdomain;

  const fromQuery = new URLSearchParams(windowObject.location.search)
    .get('commercialId')
    ?.trim();
  return fromQuery || null;
}

/**
 * Gets the full URL of the current page
 *
 * @param {Window} [windowObject=window] - The window object (for testing)
 * @returns {string} - The complete URL including protocol, host, path, query, and hash
 *
 * @example
 * // On https://jean.easyservicebf.com/dashboard?ref=twitter
 * getFullUrl(); // Returns: "https://jean.easyservicebf.com/dashboard?ref=twitter"
 */
export function getFullUrl(windowObject: Window = window): string {
  return windowObject.location.href;
}

/**
 * Gets the hostname (including subdomain)
 *
 * @param {Window} [windowObject=window] - The window object (for testing)
 * @returns {string} - The hostname (e.g., "jean.easyservicebf.com")
 *
 * @example
 * // On jean.easyservicebf.com
 * getHost(); // Returns: "jean.easyservicebf.com"
 */
export function getHost(windowObject: Window = window): string {
  return windowObject.location.hostname;
}

/**
 * Gets the main domain without subdomain
 *
 * @param {Window} [windowObject=window] - The window object (for testing)
 * @returns {string} - The main domain (e.g., "easyservicebf.com")
 *
 * @example
 * // On jean.easyservicebf.com
 * getDomain(); // Returns: "easyservicebf.com"
 */
export function getDomain(windowObject: Window = window): string {
  const hostname = windowObject.location.hostname;
  const parts = hostname.split('.');

  // If we have at least 3 parts (subdomain.domain.tld), return the last 2 parts
  if (parts.length >= 3) {
    return parts.slice(-2).join('.');
  }

  // Otherwise return the full hostname
  return hostname;
}

/**
 * Gets complete partner data from the current URL
 *
 * This is the main utility function that provides all partner-related
 * information in a single object. Use this for comprehensive partner tracking.
 *
 * @param {Window} [windowObject=window] - The window object (for testing)
 * @param {PartnerOptions} [options={}] - Configuration options
 * @returns {PartnerData} - Complete partner information object
 *
 * @example
 * // On jean.easyservicebf.com
 * const data = getPartnerData();
 * // Returns:
 * // {
 * //   partner: "jean",
 * //   url: "https://jean.easyservicebf.com/dashboard",
 * //   host: "jean.easyservicebf.com",
 * //   domain: "easyservicebf.com",
 * //   timestamp: 1736659200000,
 * //   isPartnerSite: true,
 * //   isProduction: true
 * // }
 *
 * @example
 * // On www.easyservicebf.com
 * const data = getPartnerData();
 * // Returns:
 * // {
 * //   partner: null,
 * //   url: "https://www.easyservicebf.com/",
 * //   host: "www.easyservicebf.com",
 * //   domain: "easyservicebf.com",
 * //   timestamp: 1736659200000,
 * //   isPartnerSite: false,
 * //   isProduction: true
 * // }
 */
export function getPartnerData(
  windowObject: Window = window,
  options: PartnerOptions = {}
): PartnerData {
  const config = { ...DEFAULT_CONFIG, ...options };
  const hostname = windowObject.location.hostname.toLowerCase();

  const partner = getPartnerFromUrl(windowObject, config);
  const url = getFullUrl(windowObject);
  const host = getHost(windowObject);
  const domain = getDomain(windowObject);
  const isPartnerSite = partner !== null;
  const isProduction = !isLocalhost(hostname);

  return {
    partner,
    url,
    host,
    domain,
    timestamp: config.includeTimestamp ? Date.now() : 0,
    isPartnerSite,
    isProduction,
  };
}

/**
 * Checks if the current environment is localhost
 *
 * @param {string} [hostname] - The hostname to check (defaults to current hostname)
 * @returns {boolean} - True if running on localhost
 *
 * @example
 * // On localhost:3000
 * isLocalhost(); // Returns: true
 *
 * // On jean.easyservicebf.com
 * isLocalhost(); // Returns: false
 */
export function isLocalhost(hostname?: string): boolean {
  const host = hostname || window.location.hostname.toLowerCase();
  return LOCALHOST_PATTERNS.some(pattern => host === pattern || host.startsWith(pattern + ':'));
}

/**
 * Formats partner data for API requests
 *
 * This function prepares the partner data for sending to backend APIs.
 * It removes null values and adds common metadata.
 *
 * @param {PartnerData} data - The partner data object
 * @returns {Record<string, string | number | boolean>} - Formatted data for API
 *
 * @example
 * const data = getPartnerData();
 * const apiData = formatPartnerDataForApi(data);
 * // Returns:
 * // {
 * //   partner: "jean",
 * //   url: "https://jean.easyservicebf.com/dashboard",
 * //   host: "jean.easyservicebf.com",
 * //   domain: "easyservicebf.com",
 * //   isPartnerSite: true,
 * //   isProduction: true
 * // }
 * // (timestamp is included if not zero, null values are removed)
 */
export function formatPartnerDataForApi(
  data: PartnerData
): Record<string, string | number | boolean> {
  const formatted: Record<string, string | number | boolean> = {};

  // Add all non-null, non-zero values
  if (data.partner !== null) {
    formatted.partner = data.partner;
  }

  formatted.url = data.url;
  formatted.host = data.host;
  formatted.domain = data.domain;
  formatted.isPartnerSite = data.isPartnerSite;
  formatted.isProduction = data.isProduction;

  // Only include timestamp if it's non-zero
  if (data.timestamp > 0) {
    formatted.timestamp = data.timestamp;
  }

  return formatted;
}

// ============================================================================
// UTILITY FUNCTIONS
// ============================================================================

/**
 * Generates a partner tracking URL with UTM parameters
 *
 * @param {string} baseUrl - The base URL to append parameters to
 * @param {PartnerData} data - The partner data
 * @param {string} [campaign] - Optional campaign name
 * @returns {string} - URL with UTM parameters
 *
 * @example
 * const data = getPartnerData();
 * const url = generatePartnerTrackingUrl('https://easyservicebf.com', data, 'referral-2025');
 * // Returns: "https://easyservicebf.com?utm_source=jean&utm_medium=partner&utm_campaign=referral-2025"
 */
export function generatePartnerTrackingUrl(
  baseUrl: string,
  data: PartnerData,
  campaign?: string
): string {
  const url = new URL(baseUrl);

  if (data.partner) {
    url.searchParams.set('utm_source', data.partner);
    url.searchParams.set('utm_medium', 'partner');
    if (campaign) {
      url.searchParams.set('utm_campaign', campaign);
    }
  }

  return url.toString();
}

/**
 * Validates if a given string is a valid partner subdomain
 *
 * @param {string} subdomain - The subdomain to validate
 * @returns {boolean} - True if valid partner subdomain
 *
 * @example
 * isValidPartnerSubdomain('jean'); // Returns: true
 * isValidPartnerSubdomain('jean-pierre'); // Returns: true
 * isValidPartnerSubdomain('123'); // Returns: true
 * isValidPartnerSubdomain('www'); // Returns: false (ignored)
 * isValidPartnerSubdomain(''); // Returns: false
 * isValidPartnerSubdomain('invalid subdomain'); // Returns: false
 */
export function isValidPartnerSubdomain(subdomain: string): boolean {
  if (!subdomain || subdomain.length === 0) {
    return false;
  }

  // Check for invalid characters (only alphanumeric and hyphens allowed)
  const validPattern = /^[a-z0-9-]+$/i;
  if (!validPattern.test(subdomain)) {
    return false;
  }

  // Check for ignored subdomains
  const ignoredSubdomains = ['www', 'api', 'admin', 'mail', 'ftp', 'cdn'];
  if (ignoredSubdomains.includes(subdomain.toLowerCase())) {
    return false;
  }

  // Check for valid length (1-63 characters per DNS spec)
  if (subdomain.length > 63) {
    return false;
  }

  // Check that it doesn't start or end with a hyphen
  if (subdomain.startsWith('-') || subdomain.endsWith('-')) {
    return false;
  }

  return true;
}

/**
 * Creates a masked version of partner data for logging
 * (Useful for debugging without exposing sensitive information)
 *
 * @param {PartnerData} data - The partner data
 * @returns {Record<string, string>} - Masked partner data
 *
 * @example
 * const data = getPartnerData();
 * const masked = maskPartnerData(data);
 * // Returns: { partner: "j***n", domain: "e***********m" }
 */
export function maskPartnerData(data: PartnerData): Record<string, string> {
  const masked: Record<string, string> = {};

  if (data.partner) {
    const first = data.partner.charAt(0);
    const last = data.partner.charAt(data.partner.length - 1);
    masked.partner = data.partner.length > 2
      ? `${first}${'*'.repeat(data.partner.length - 2)}${last}`
      : `${first}*`;
  } else {
    masked.partner = '(none)';
  }

  masked.domain = data.domain.length > 4
    ? `${data.domain.substring(0, 1)}${'*'.repeat(data.domain.length - 4)}${data.domain.substring(data.domain.length - 3)}`
    : data.domain;

  return masked;
}

// ============================================================================
// EXPORTS
// ============================================================================

export default {
  getPartnerFromUrl,
  getCommercialIdFromPath,
  getCommercialId,
  getFullUrl,
  getHost,
  getDomain,
  getPartnerData,
  isLocalhost,
  formatPartnerDataForApi,
  generatePartnerTrackingUrl,
  isValidPartnerSubdomain,
  maskPartnerData,
};
